import * as TonalInterval from '@tonaljs/interval';
import * as TonalScale from '@tonaljs/scale';

import { TheoryServiceError } from '../errors';
import { ScaleDegreeInfo } from '../types';
import { calculateScaleDegrees } from './scale-calculations';
import { SPECIAL_SCALES } from './scale-constants/special-scales';

/**
 * Gets notes for a scale
 * @param root - Root note (e.g. "C", "F#")
 * @param type - Scale type (e.g. "major", "minor", "dorian")
 * @returns Array of note names
 */
export function getScaleNotes(root: string, type: string): string[] {
  try {
    const scale = TonalScale.get(`${root} ${type}`);
    if (!scale.empty) {
      return scale.notes;
    }
    throw new TheoryServiceError(`Invalid scale: ${root} ${type}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new TheoryServiceError(`Error getting scale: ${message}`);
  }
}

/**
 * Gets a complete scale object with additional properties
 * @param root - Root note (e.g. "C", "F#")
 * @param type - Scale type (e.g. "major", "minor", "dorian")
 * @returns Scale object from TonalJS
 */
export function getScale(root: string, type: string) {
  try {
    const scale = TonalScale.get(`${root} ${type}`);
    if (!scale.empty) {
      return scale;
    }
    throw new TheoryServiceError(`Invalid scale: ${root} ${type}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new TheoryServiceError(`Error getting scale: ${message}`);
  }
}

/**
 * Finds scales that contain the given notes
 * @param notes - Array of note names
 * @returns Array of possible scale names
 */
export function findScales(notes: string[]): string[] {
  return TonalScale.detect(notes);
}

/**
 * Gets scale degree information for a scale
 * @param rootNote - Root note (e.g. "C", "F#")
 * @param scaleType - Scale type (e.g. "major", "minor", "pentatonic")
 * @returns Array of scale degree information objects
 */
export function getScaleDegreeInfo(
  rootNote: string,
  scaleType: string,
  areNotesEnharmonic: (noteA: string, noteB: string) => boolean,
  getInterval: (noteA: string, noteB: string) => string
): ScaleDegreeInfo[] {
  // First check for special scales that need custom handling
  const normalizedScaleType = scaleType.toLowerCase();
  if (SPECIAL_SCALES[normalizedScaleType]) {
    return SPECIAL_SCALES[normalizedScaleType].degrees;
  }

  // Get scale notes from TonalJS
  const scaleNotes = getScaleNotes(rootNote, scaleType);
  // Get reference major scale notes for comparison
  const majorScaleNotes = getScaleNotes(rootNote, 'major');

  // For standard scales, calculate the degrees
  return calculateScaleDegrees(
    rootNote,
    scaleType,
    areNotesEnharmonic,
    getInterval,
    scaleNotes,
    majorScaleNotes
  );
}

/**
 * Gets comprehensive information about a scale
 * @param rootNote The root note of the scale
 * @param scaleType The type of scale
 * @returns Object with detailed scale information
 */
export function getScaleInfo(
  rootNote: string,
  scaleType: string,
  areNotesEnharmonic: (noteA: string, noteB: string) => boolean,
  getInterval: (noteA: string, noteB: string) => string,
  formatTraditionalInterval: (interval: string | undefined) => string
): {
  notes: string[];
  degrees: string[];
  standardDegrees: number[];
  semitones: number[];
  stepPattern: string[];
} {
  const scaleNotes = getScaleNotes(rootNote, scaleType);
  const degreeInfo = getScaleDegreeInfo(rootNote, scaleType, areNotesEnharmonic, getInterval);

  // Format degrees and get standard degrees
  const degrees = scaleNotes.map((_, idx) => {
    if (idx < degreeInfo.length) {
      const info = degreeInfo[idx];
      return info ? info.display : String(idx + 1);
    }
    return String(idx + 1);
  });

  const standardDegrees = scaleNotes.map((_, idx) => {
    if (idx < degreeInfo.length) {
      const info = degreeInfo[idx];
      return info ? info.degree : (idx % 7) + 1;
    }
    return (idx % 7) + 1;
  });

  // Get semitones from root
  const semitones = scaleNotes.map((note, idx) => {
    if (idx < degreeInfo.length) {
      const info = degreeInfo[idx];
      return info ? info.semitones : TonalInterval.semitones(getInterval(rootNote, note)) || 0;
    }
    return TonalInterval.semitones(getInterval(rootNote, note)) || 0;
  });

  // Create step pattern (whole/half steps)
  const stepPattern = [];
  for (let i = 0; i < scaleNotes.length - 1; i++) {
    const a = scaleNotes[i];
    const b = scaleNotes[i + 1];
    if (!a || !b) {
      throw new TheoryServiceError('Unexpected missing scale note while building step pattern');
    }
    const interval = getInterval(a, b);
    if (interval === '2M') {
      stepPattern.push('W'); // Whole step
    } else if (interval === '2m' || interval === '1A') {
      stepPattern.push('H'); // Half step (minor second or augmented unison)
    } else if (interval === '3m') {
      stepPattern.push('m3'); // Minor third (special case for pentatonic scales)
    } else {
      // For other intervals, use traditional format
      stepPattern.push(formatTraditionalInterval(interval));
    }
  }

  return {
    notes: scaleNotes,
    degrees,
    standardDegrees,
    semitones,
    stepPattern,
  };
}
